import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getDatabase, ref, get, update } from 'firebase/database';
import toast from 'react-hot-toast';
import Loading from './common/Loading';
import logo from '../assets/ma2.png';
import styles from '../styles/viewlocal.module.css';

// ===== ICONOS =====
import { 
  FaArrowLeft,
  FaClock,
  FaTag,
  FaMoneyBillWave,
  FaTruck,
  FaPhone,
  FaHeart,
  FaRegHeart,
  FaFlag,
  FaCreditCard,
  FaUniversity,
  FaMobile,
  FaCheckCircle,
  FaTimesCircle,
  FaImage,
  FaTimes,
  FaInstagram,
  FaWhatsapp,
  FaStore
} from 'react-icons/fa';

// ✅ FUNCIÓN SEGURA PARA OBTENER LIKES
const getLikesValue = (likes) => {
  if (likes === null || likes === undefined) return 0;
  if (typeof likes === 'number') return likes;
  if (typeof likes === 'object') {
    return likes.acumulado || likes.diario || 0;
  }
  return 0;
};

const ViewLocal = () => {
  const { mercadoId, localId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [local, setLocal] = useState(null);
  const [mercado, setMercado] = useState(null);
  const [error, setError] = useState(null);
  const [likesDiarios, setLikesDiarios] = useState(0);
  const [likesAcumulados, setLikesAcumulados] = useState(0);
  const [yaDioLike, setYaDioLike] = useState(false);
  const [modalAbierto, setModalAbierto] = useState(false);

  // Cargar datos del local
  useEffect(() => {
    const cargarDatos = async () => {
      try {
        setLoading(true);
        setError(null);
        const db = getDatabase();
        
        const mercadoSnap = await get(ref(db, `mercados/${mercadoId}`));
        if (!mercadoSnap.exists()) {
          setError('Mercado no encontrado');
          setLoading(false);
          return;
        }
        setMercado({ id: mercadoId, ...mercadoSnap.val() });
        
        const localSnap = await get(ref(db, `mercados/${mercadoId}/locales/${localId}`));
        if (!localSnap.exists()) {
          setError('Local no encontrado');
          setLoading(false);
          return;
        }
        const localData = { id: localId, ...localSnap.val() };
        setLocal(localData);
        
        // ✅ Usar getLikesValue para evitar objetos
        setLikesDiarios(getLikesValue(localData.likesDiarios));
        setLikesAcumulados(getLikesValue(localData.likes));
        
        const hoy = new Date().toISOString().split('T')[0];
        const userKey = `user_${localId}_${hoy}`;
        const yaDio = localStorage.getItem(userKey) === 'true';
        setYaDioLike(yaDio);
        
      } catch (error) {
        console.error('Error cargando datos:', error);
        setError('Error al cargar los datos. Intenta de nuevo.');
      } finally {
        setLoading(false);
      }
    };
    
    if (mercadoId && localId) {
      cargarDatos();
    }
  }, [mercadoId, localId]);

  // Verificar si el local está abierto
  const isLocalAbierto = () => {
    if (!local?.horarios) return false;
    const ahora = new Date();
    const dias = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'];
    const diaActual = dias[ahora.getDay()];
    const horaActual = ahora.getHours().toString().padStart(2, '0') + ':' + ahora.getMinutes().toString().padStart(2, '0');
    
    const horario = local.horarios[diaActual];
    if (!horario || !horario.abierto) return false;
    
    return horaActual >= horario.apertura && horaActual <= horario.cierre;
  };

  // Manejar like (guardando dentro del local)
  const handleLike = async () => {
    if (yaDioLike) {
      toast.error('Ya has dado like hoy. Vuelve mañana.');
      return;
    }
    
    const hoy = new Date().toISOString().split('T')[0];
    const userKey = `user_${localId}_${hoy}`;
    
    try {
      const db = getDatabase();
      
      const localRef = ref(db, `mercados/${mercadoId}/locales/${localId}`);
      const nuevosDiarios = likesDiarios + 1;
      const nuevosAcumulados = likesAcumulados + 1;
      
      await update(localRef, { 
        likes: nuevosAcumulados,
        likesDiarios: nuevosDiarios,
        ultimoLike: new Date().toISOString()
      });
      
      setLikesDiarios(nuevosDiarios);
      setLikesAcumulados(nuevosAcumulados);
      setYaDioLike(true);
      localStorage.setItem(userKey, 'true');
      
      toast.success('❤️ ¡Gracias por tu like!');
      
    } catch (error) {
      console.error('Error al dar like:', error);
      toast.error('Error al guardar tu like');
    }
  };

  // Reportar local
  const handleReportar = () => {
    toast(
      (t) => (
        <div style={{ textAlign: 'center', padding: '8px 4px', color: '#1F2937' }}>
          <p style={{ margin: '0 0 8px', fontWeight: 600, fontSize: '16px' }}>
            📧 Reportar local
          </p>
          <p style={{ margin: '0 0 4px', fontSize: '14px' }}>
            Envía un correo a:
          </p>
          <p style={{ 
            margin: '0 0 12px', 
            fontSize: '18px', 
            fontWeight: 700, 
            color: '#43115E',
            background: '#f3e8f7',
            padding: '6px 16px',
            borderRadius: '8px',
            display: 'inline-block'
          }}>
            mercadoabierto@email.com
          </p>
          <p style={{ margin: '0', fontSize: '12px', color: '#6B7280' }}>
            Incluye el nombre del local y el motivo del reporte.
          </p>
          <button
            onClick={() => toast.dismiss(t.id)}
            style={{
              marginTop: '12px',
              padding: '6px 24px',
              background: '#43115E',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontFamily: 'Poppins, sans-serif'
            }}
          >
            Entendido
          </button>
        </div>
      ),
      {
        duration: 8000,
        style: {
          maxWidth: '400px',
          background: 'white',
          borderRadius: '16px',
          boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
          padding: '20px'
        }
      }
    );
  };

  // Renderizar corazones
  const renderCorazones = (cantidad) => {
    const valor = getLikesValue(cantidad); // ✅ Asegurar que sea número
    const max = 5;
    const llenos = Math.min(valor, max);
    const vacios = max - llenos;
    return (
      <span className={styles.corazonesDisplay}>
        {Array.from({ length: llenos }, (_, i) => (
          <FaHeart key={`full-${i}`} className={styles.corazonLleno} />
        ))}
        {Array.from({ length: vacios }, (_, i) => (
          <FaRegHeart key={`empty-${i}`} className={styles.corazonVacio} />
        ))}
      </span>
    );
  };

  const diasSemana = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'];
  const diaHoy = diasSemana[new Date().getDay()];
  const diasMap = {
    lunes: 'Lunes', martes: 'Martes', miercoles: 'Miércoles',
    jueves: 'Jueves', viernes: 'Viernes', sabado: 'Sábado', domingo: 'Domingo'
  };

  const abrirModal = () => setModalAbierto(true);
  const cerrarModal = () => setModalAbierto(false);

  if (loading) {
    return (
      <>
        <header className={styles.viewHeader}>
          <div className={styles.viewHeaderContainer}>
            <Link to="/" className={styles.viewLogo}>
              <img src={logo} alt="Mercado Abierto" className={styles.viewLogoImg} />
            </Link>
          </div>
        </header>
        <Loading message="Cargando local..." />
        <footer className={styles.viewFooter}>
          <div className={styles.viewFooterContent}>
            <p>© {new Date().getFullYear()} mercadoabierto.online</p>
          </div>
        </footer>
      </>
    );
  }

  if (error || !local) {
    return (
      <>
        <header className={styles.viewHeader}>
          <div className={styles.viewHeaderContainer}>
            <Link to="/" className={styles.viewLogo}>
              <img src={logo} alt="Mercado Abierto" className={styles.viewLogoImg} />
            </Link>
          </div>
        </header>
        <div className={styles.errorContainer}>
          <p className={styles.errorMessage}>{error || 'Local no encontrado'}</p>
          <button onClick={() => navigate(`/search/${mercadoId}`)} className={styles.reloadButton}>
            ← Volver al mercado
          </button>
        </div>
        <footer className={styles.viewFooter}>
          <div className={styles.viewFooterContent}>
            <p>© {new Date().getFullYear()} mercadoabierto.online</p>
          </div>
        </footer>
      </>
    );
  }

  const abierto = isLocalAbierto();

  return (
    <div className={styles.viewContainer}>

      {/* HEADER */}
      <header className={styles.viewHeader}>
        <div className={styles.viewHeaderContainer}>
          <Link to="/" className={styles.viewLogo}>
            <img src={logo} alt="Mercado Abierto" className={styles.viewLogoImg} />
          </Link>
        </div>
      </header>

      <main className={styles.viewMainContent}>

        {/* VOLVER */}
        <button onClick={() => navigate(`/search/${mercadoId}`)} className={styles.backButton}>
          <FaArrowLeft size={14} />
          Volver al mercado
        </button>

        {/* HERO */}
        <section
          className={styles.hero}
          style={{ backgroundImage: `url(${local.portada || '/img/default-local.jpg'})` }}
        >
          <div className={styles.heroOverlay}>
            <div className={styles.heroContent}>
              <h1>{local.nombre}</h1>

              <div className={styles.heroMercado}>
                <FaStore size={16} />
                <span>{mercado?.nombre || 'Mercado'}</span>
              </div>

              <div className={styles.heroMeta}>
                {local.numeroLocal && (
                  <span className={styles.heroMetaItem}>📍 #{local.numeroLocal}</span>
                )}
                {local.seccion && (
                  <span className={styles.heroMetaItem}>🏷️ {local.seccion}</span>
                )}
              </div>

              <div className={styles.heroStatus}>
                {abierto
                  ? <span className={styles.open}>● Abierto ahora</span>
                  : <span className={styles.closed}>● Cerrado</span>
                }
              </div>

              <div className={styles.heroLikes}>
                <span className={styles.heroLikesDisplay}>
                  {renderCorazones(likesAcumulados)}
                  <span className={styles.heroLikesCount}>{getLikesValue(likesAcumulados)} likes</span>
                </span>
              </div>

              <button
                className={styles.likeButtonHero}
                onClick={handleLike}
                disabled={yaDioLike}
              >
                <FaHeart size={16} />
                {yaDioLike ? 'Ya diste like' : 'Dar like'}
              </button>

              {local.fotoExtra && (
                <button
                  className={styles.fotoExtraBtn}
                  onClick={abrirModal}
                >
                  <FaImage size={14} />
                  Ver foto adicional
                </button>
              )}
            </div>
          </div>
        </section>

        {/* MODAL */}
        {modalAbierto && local.fotoExtra && (
          <div className={styles.modalOverlay} onClick={cerrarModal}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <button className={styles.modalClose} onClick={cerrarModal}>
                <FaTimes size={24} />
              </button>
              <img 
                src={local.fotoExtra} 
                alt="Foto adicional" 
                className={styles.modalImage} 
              />
              <p className={styles.modalCaption}>📸 Foto adicional de {local.nombre}</p>
            </div>
          </div>
        )}

        {/* HORARIOS */}
        <div className={styles.card}>
          <h2><FaClock size={17} /> Horarios</h2>
          <div className={styles.horariosGrid}>
            {local.horarios
              ? Object.entries(local.horarios).map(([dia, horario]) => {
                  const esHoy = dia === diaHoy;
                  return (
                    <div key={dia} className={`${styles.horarioItem} ${esHoy ? styles.hoy : ''}`}>
                      <span className={styles.horarioDia}>
                        {diasMap[dia] || dia}
                        {esHoy && <span className={styles.hoyPill}>Hoy</span>}
                      </span>
                      {horario.abierto
                        ? <span className={`${styles.horarioHora} ${esHoy ? styles.hoyHora : ''}`}>
                            {horario.apertura} - {horario.cierre}
                          </span>
                        : <span className={styles.horarioCerrado}>Cerrado</span>
                      }
                    </div>
                  );
                })
              : <p className={styles.noData}>Sin horarios disponibles</p>
            }
          </div>
        </div>

        {/* PRODUCTOS + MÉTODOS DE PAGO */}
        <div className={styles.cardGrid}>
          {local.productos && local.productos.length > 0 && (
            <div className={styles.card}>
              <h2><FaTag size={15} /> Productos</h2>
              <div className={styles.productosList}>
                {local.productos.slice(0, 6).map((p, i) => (
                  <span key={i} className={styles.productoTag}>{p}</span>
                ))}
              </div>
              {local.productos.length > 6 && (
                <button className={styles.verMas}>+ Ver más</button>
              )}
            </div>
          )}

          {local.metodosPago && (
            <div className={styles.card}>
              <h2><FaMoneyBillWave size={15} /> Métodos de pago</h2>
              <div className={styles.pagosList}>
                {local.metodosPago.efectivo && (
                  <span className={styles.pagoTag}><FaMoneyBillWave size={13} /> Efectivo</span>
                )}
                {local.metodosPago.tarjeta && (
                  <span className={styles.pagoTag}><FaCreditCard size={13} /> Tarjeta</span>
                )}
                {local.metodosPago.transferencia && (
                  <span className={styles.pagoTag}><FaUniversity size={13} /> Transferencia</span>
                )}
                {local.metodosPago.codi && (
                  <span className={styles.pagoTag}><FaMobile size={13} /> CoDi</span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ENVÍOS + CONTACTO */}
        <div className={styles.cardGrid}>
          <div className={styles.card}>
            <h2><FaTruck size={15} /> Envíos</h2>
            <p className={`${styles.enviosText} ${!local.hacenEnvios ? styles.noEnvios : ''}`}>
              {local.hacenEnvios
                ? <><FaCheckCircle size={15} /> Realizan envíos a domicilio</>
                : <><FaTimesCircle size={15} /> No realizan envíos</>
              }
            </p>
          </div>

          {local.telefonoContacto && (
            <div className={styles.card}>
              <h2><FaPhone size={15} /> Contacto</h2>
              <p className={styles.telefonoText}>
                <a href={`tel:${local.telefonoContacto}`} className={styles.telefonoLink}>
                  <FaPhone size={13} />
                  {local.telefonoContacto}
                </a>
              </p>
            </div>
          )}
        </div>

        {/* REPORTAR */}
        <button onClick={handleReportar} className={styles.reportButton}>
          <FaFlag size={15} />
          Reportar local
        </button>

      </main>

      {/* FOOTER */}
      <footer className={styles.viewFooter}>
        <div className={styles.viewFooterGrid}>
          <div>
            <img src={logo} alt="logo" className={styles.viewFooterLogo} />
            <p>Plataforma digital que conecta a los mercados municipales de México contigo.</p>
          </div>
          <div>
            <h4>Enlaces rápidos</h4>
            <p onClick={() => navigate('/')}>Inicio</p>
            <p onClick={() => navigate('/')}>Explorar mercados</p>
            <p onClick={() => navigate('/privacidad')}>Aviso de Privacidad</p>
            <p onClick={() => navigate('/terminos')}>Términos y Condiciones</p>
          </div>
          <div>
            <h4>Síguenos</h4>
            <div className={styles.socials}>
              <FaInstagram size={28} />
              <FaWhatsapp size={28} />
            </div>
          </div>
        </div>
        <div className={styles.mapFooterBottom}>
          © {new Date().getFullYear()} mercadoabierto.online - Todos los derechos reservados
        </div>
      </footer>

    </div>
  );
};

export default ViewLocal;